"use client"

import React from "react"

import { useState, useEffect, useRef, useCallback } from "react"
import { Input } from "@/components/ui/input"

const ASCII_ART_ORIGINAL = `███████╗██╗   ██╗████████╗ ██████╗ ██╗   ██╗██╗ █████╗ 
██╔════╝██║   ██║╚══██╔══╝██╔═══██╗██║   ██║██║██╔══██╗
█████╗  ██║   ██║   ██║   ██║   ██║██║   ██║██║███████║
██╔══╝  ██║   ██║   ██║   ██║   ██║╚██╗ ██╔╝██║██╔══██║
██║     ╚██████╔╝   ██║   ╚██████╔╝ ╚████╔╝ ██║██║  ██║
╚═╝      ╚═════╝    ╚═╝    ╚═════╝   ╚═══╝  ╚═╝╚═╝  ╚═╝`

// Characters to use for ASCII scramble effect
const SCRAMBLE_CHARS = "█▓▒░╔╗╚╝║═╬╣╠╩╦├┤┴┬│─┼▀▄■□▪▫"

const FILES: Record<string, string> = {
  "help.md": `# FUTOVIA TERMINAL HELP

## Available Commands
- ls          List files in current directory
- cat <file>  Display file contents
- pwd         Print working directory
- whoami      Display current user
- date        Show current date/time
- echo <msg>  Print message to terminal
- clear       Clear the terminal
- scramble    Trigger visual scramble effect
- help        Show this help message

## Available Files
- help.md
- about.md
- contact.md
- socials.md`,

  "about.md": `# FUTOVIA

We build AI products. Some you'll see, most you won't.

Singapore. Since 2021.

hello@futovia.com`,

  "contact.md": `# CONTACT

hello@futovia.com

Tell us what you're building.`,

  "socials.md": `# SOCIALS

X: x.com/Futoviadotcom
LinkedIn: linkedin.com/company/futovia-pte-ltd
Instagram: instagram.com/futovia

Follow us for updates and announcements.`,
}

const MAX_LINES = 20

export function AsciiHero() {
  const [lines, setLines] = useState<string[]>([])
  const [inputValue, setInputValue] = useState("")
  const [isReady, setIsReady] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const terminalRef = useRef<HTMLDivElement>(null)
  const scrambleIntervalRef = useRef<NodeJS.Timeout | null>(null)

  // Scramble ASCII art with animated progression through characters
  const scrambleAscii = useCallback(() => {
    const originalLines = ASCII_ART_ORIGINAL.split("\n")
    const totalIterations = 12
    let iteration = 0
    
    const scrambleIteration = () => {
      if (iteration >= totalIterations) {
        // Final pass - restore original
        setLines(prev => {
          const newLines = [...prev]
          for (let i = 0; i < Math.min(originalLines.length, newLines.length); i++) {
            if (newLines[i] && (newLines[i].includes("█") || newLines[i].includes("╔") || newLines[i].includes("║") || newLines[i].includes("╚") || newLines[i].includes("═") || newLines[i].includes("▓") || newLines[i].includes("▒") || newLines[i].includes("░") || newLines[i].includes("▀") || newLines[i].includes("▄"))) {
              newLines[i] = originalLines[i]
            }
          }
          return newLines
        })
        return
      }
      
      // Progress ratio - more chars settle as we go
      const settleRatio = iteration / totalIterations
      
      setLines(prev => {
        const newLines = [...prev]
        for (let i = 0; i < Math.min(originalLines.length, newLines.length); i++) {
          if (newLines[i] && (newLines[i].includes("█") || newLines[i].includes("╔") || newLines[i].includes("║") || newLines[i].includes("╚") || newLines[i].includes("═") || newLines[i].includes("▓") || newLines[i].includes("▒") || newLines[i].includes("░") || newLines[i].includes("▀") || newLines[i].includes("▄"))) {
            const originalLine = originalLines[i]
            newLines[i] = originalLine.split("").map((char, charIndex) => {
              // Skip spaces
              if (char === " ") return char
              // Progressively settle from left to right
              const charSettlePoint = charIndex / originalLine.length
              if (charSettlePoint < settleRatio || Math.random() < settleRatio * 0.5) {
                return char
              }
              // Still scrambling
              if (Math.random() < 0.7) {
                return SCRAMBLE_CHARS[Math.floor(Math.random() * SCRAMBLE_CHARS.length)]
              }
              return char
            }).join("")
          }
        }
        return newLines
      })
      
      iteration++
      setTimeout(scrambleIteration, 50)
    }
    
    scrambleIteration()
  }, [])

  // Set up occasional ASCII scramble
  useEffect(() => {
    const scheduleScramble = () => {
      const delay = 5000 + Math.random() * 10000 // 5-15 seconds
      scrambleIntervalRef.current = setTimeout(() => {
        scrambleAscii()
        scheduleScramble()
      }, delay)
    }
    
    // Start after initial boot
    const startDelay = setTimeout(() => {
      scheduleScramble()
    }, 3000)
    
    return () => {
      clearTimeout(startDelay)
      if (scrambleIntervalRef.current) clearTimeout(scrambleIntervalRef.current)
    }
  }, [scrambleAscii])

  // Boot sequence — extracted so we can re-run on dark-mode toggle
  const runBootSequence = useCallback(async () => {
    setLines([])
    setInputValue("")
    setIsReady(false)

    const asciiLines = ASCII_ART_ORIGINAL.split("\n")

    for (let i = 0; i < asciiLines.length; i++) {
      await new Promise(resolve => setTimeout(resolve, 80))
      setLines(prev => [...prev, asciiLines[i]])
    }

    await new Promise(resolve => setTimeout(resolve, 200))
    setLines(prev => [...prev, ""])
    await new Promise(resolve => setTimeout(resolve, 100))
    setLines(prev => [...prev, "System initialized..."])
    await new Promise(resolve => setTimeout(resolve, 150))
    setLines(prev => [...prev, "Welcome to FUTOVIA terminal v2.2.4"])
    await new Promise(resolve => setTimeout(resolve, 100))
    setLines(prev => [...prev, "> type 'help' to look around"])
    await new Promise(resolve => setTimeout(resolve, 200))
    setLines(prev => [...prev, ""])
    await new Promise(resolve => setTimeout(resolve, 100))
    setLines(prev => [...prev, "┌────────────────┐"])
    await new Promise(resolve => setTimeout(resolve, 50))
    setLines(prev => [...prev, "│  DEPLOY AGENT  │"])
    await new Promise(resolve => setTimeout(resolve, 50))
    setLines(prev => [...prev, "└────────────────┘"])
    await new Promise(resolve => setTimeout(resolve, 100))
    setLines(prev => [...prev, ""])

    setIsReady(true)
  }, [])

  // Initial boot on mount
  useEffect(() => {
    runBootSequence()
  }, [runBootSequence])

  useEffect(() => {
    if (isReady && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isReady])

  // Keep lines trimmed to max and scroll to bottom
  useEffect(() => {
    if (lines.length > MAX_LINES) {
      setLines(prev => prev.slice(-MAX_LINES))
    }
    // Auto-scroll to bottom
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight
    }
  }, [lines])

  const addLines = (...newLines: string[]) => {
    setLines(prev => [...prev, ...newLines])
  }

  const handleCommand = (e: React.FormEvent) => {
    e.preventDefault()
    const cmd = inputValue.trim()
    if (!cmd) return

    // Echo the command
    addLines(`> ${cmd}`)

    const parts = cmd.split(" ")
    const command = parts[0].toLowerCase()
    const args = parts.slice(1).join(" ")

    switch (command) {
      case "help":
        addLines(
          "Available commands:",
          "  ls [-la]      List directory contents",
          "  cat <file>    Display file contents",
          "  pwd           Print working directory",
          "  whoami        Display current user",
          "  date          Display current date/time",
          "  echo <text>   Display text",
          "  uname [-a]    Print system information",
          "  uptime        Show system uptime",
          "  clear         Clear terminal",
          "  scramble      ???",
          "",
          "Files: about.md, contact.md, help.md, socials.md"
        )
        break

      case "scramble":
        addLines("Restarting server connection...")
        scrambleAscii()
        break

      case "ls":
        if (args === "-la" || args === "-l" || args === "-al") {
          addLines(
            "total 4",
            "-rw-r--r-- 1 guest guest  512 Jan 30 00:00 about.md",
            "-rw-r--r-- 1 guest guest  256 Jan 30 00:00 contact.md",
            "-rw-r--r-- 1 guest guest  384 Jan 30 00:00 help.md",
            "-rw-r--r-- 1 guest guest  128 Jan 30 00:00 socials.md"
          )
        } else {
          addLines("about.md  contact.md  help.md  socials.md")
        }
        break

      case "cat":
        if (!args) {
          addLines("Usage: cat <filename>")
        } else {
          const filename = args.toLowerCase()
          if (FILES[filename]) {
            const fileLines = FILES[filename].split("\n")
            addLines(...fileLines)
          } else {
            addLines(`cat: ${args}: No such file or directory`)
          }
        }
        break

      case "pwd":
        addLines("/home/guest/futovia")
        break

      case "whoami":
        addLines("neo@futovia")
        break

      case "date":
        addLines(new Date().toString())
        break

      case "echo":
        // Strip surrounding quotes like a real shell
        const echoOutput = args ? args.replace(/^["'](.*)["']$/, "$1") : ""
        addLines(echoOutput)
        break

      case "clear":
        setTimeout(() => runBootSequence(), 100)
        return

      case "cd":
        if (!args || args === "~") {
          addLines("") // cd with no args or ~ does nothing visibly
        } else {
          addLines(`bash: cd: ${args}: No such file or directory`)
        }
        break

      case "uname":
        if (args === "-a") {
          addLines("FUTOVIA 1.0.0 futovia-terminal x86_64 GNU/Linux")
        } else {
          addLines("FUTOVIA")
        }
        break

      case "hostname":
        addLines("futovia-terminal")
        break

      case "uptime":
        const now = new Date()
        const timeStr = now.toLocaleTimeString("en-US", { hour12: false })
        const days = Math.floor(Math.random() * 1000) + 100
        const hours = Math.floor(Math.random() * 24)
        const mins = Math.floor(Math.random() * 60)
        const users = Math.floor(Math.random() * 400) + 100
        const load1 = (Math.random() * 2).toFixed(2)
        const load5 = (Math.random() * 1.5).toFixed(2)
        const load15 = (Math.random() * 1).toFixed(2)
        addLines(` ${timeStr} up ${days} days, ${hours}:${String(mins).padStart(2, "0")}, ${users} users, load average: ${load1}, ${load5}, ${load15}`)
        break

      case "man":
        addLines(`No manual entry for ${args || "man"}`)
        break

      case "rm":
      case "mv":
      case "cp":
      case "mkdir":
      case "touch":
        addLines(`bash: ${command}: Permission denied`)
        break

      case "sudo":
        addLines("[sudo] password for guest: ")
        setTimeout(() => addLines("guest is not in the sudoers file. This incident will be reported."), 500)
        break

      case "exit":
      case "quit":
        addLines("logout")
        setTimeout(() => addLines("There is no escape from the Matrix."), 300)
        break

      case "":
        // Empty command, do nothing
        break

      default:
        addLines(`bash: ${command}: command not found`)
    }

    setInputValue("")
  }

  return (
    <section className="flex h-[calc(100dvh-10rem)] w-full max-w-full flex-col overflow-x-hidden md:h-[calc(100dvh-12rem)]">
      {/* Terminal output area - fixed height with scroll */}
      <div
        ref={terminalRef}
        className="flex-1 overflow-y-auto font-mono text-xs leading-relaxed sm:text-sm"
      >
        <div className="flex min-h-full flex-col justify-end">
          {lines.map((line, i) => {
            const isStartBuildingBox = line.includes("DEPLOY AGENT") || line === "┌────────────────┐" || line === "└────────────────┘"
            const isAsciiArt = line.startsWith("█") || line.startsWith("╔") || line.startsWith("║") || line.startsWith("╚")

            if (isStartBuildingBox) {
              return (
                <div key={i}>
                  <a
                    href="/contact"
                    className="inline-block text-primary brightness-125 transition-all hover:brightness-150"
                  >
                    <pre className="whitespace-pre">{line}</pre>
                  </a>
                </div>
              )
            }

            return (
              <div
                key={i}
                className={
                  line.startsWith(">")
                    ? "text-primary"
                    : line.startsWith("#")
                      ? "font-bold text-primary"
                      : isAsciiArt
                        ? "text-primary"
                        : "text-foreground/80"
                }
              >
                <pre className={`whitespace-pre ${isAsciiArt ? "text-[10px] sm:text-sm" : ""}`}>{line || " "}</pre>
              </div>
            )
          })}
        </div>
      </div>

      {/* Input area - fixed at bottom */}
      <div className="mt-4 shrink-0">
        <form onSubmit={handleCommand} className="flex items-center">
          <span className="font-mono text-sm text-primary">guest@futovia:~$</span>
          <div className="relative ml-2 min-w-0 flex-1">
            <Input
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              className="h-8 w-full border-0 bg-transparent px-0 font-mono text-sm text-foreground caret-transparent placeholder:text-muted-foreground focus-visible:ring-0"
              placeholder={isReady && !isFocused ? "Enter command..." : ""}
              autoComplete="off"
              spellCheck={false}
              disabled={!isReady}
            />
            {isFocused && (
              <span 
                className="pointer-events-none absolute top-1/2 -translate-y-1/2 animate-blink font-mono text-sm text-primary"
                style={{ left: `${inputValue.length * 8}px` }}
              >
                █
              </span>
            )}
          </div>
        </form>
      </div>
    </section>
  )
}
